/** Ported from `kaira-mobile-app/src/utils/format.ts`. */

export function formatCurrency(amount: number): string {
  if (Number.isNaN(amount)) return '₹0';
  const rounded = Math.round(amount);
  return `₹${rounded.toLocaleString('en-IN')}`;
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
}

export function formatDate(date: Date): string {
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** `2026-09-12` -> `12 Sep 2026`. Safe against malformed input. */
export function formatDateString(value: string): string {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : formatDate(d);
}

export function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/** "12 minutes ago", "3 days ago". Used in notification and review lists. */
export function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;

  const diffMs = Date.now() - then;
  const mins = Math.round(diffMs / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;

  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hr ago`;

  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;

  const months = Math.round(days / 30);
  return `${months} month${months === 1 ? '' : 's'} ago`;
}

export function getGreeting(now: Date = new Date()): string {
  const h = now.getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

/** Initials for the avatar fallback, e.g. "Aditi R." -> "AR". */
export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');
}

/*
 * GST is not computed here: the rate is `settings.taxRatePercent`, and the
 * rounding lives with the price preview in `lib/packages.ts` (`percentOf`).
 */

/** Signed number with a true minus sign, e.g. "+3" or "−2". */
export function formatSigned(value: number): string {
  if (value > 0) return `+${value.toLocaleString('en-IN')}`;
  if (value < 0) return `\u2212${Math.abs(value).toLocaleString('en-IN')}`;
  return '0';
}

/** Tailwind-friendly class joiner. Mirrors `@utils/cn` on native. */
export function cn(...values: (string | false | null | undefined)[]): string {
  return values.filter(Boolean).join(' ');
}
